/**
 * Unified status → Tailwind color mapping used across all accent cards.
 *
 * Handles both typed enum values ("running", "succeeded") and free-form
 * strings that may contain keywords ("in progress", "doing", "done").
 */

export interface StatusDotConfig {
  color: string;
  pulse: boolean;
}

const ACCENT_MAP: Record<string, string> = {
  backlog: "border-l-gray-400",
  blocked: "border-l-gray-300",
  cancelled: "border-l-amber-500",
  completed: "border-l-emerald-500",
  doing: "border-l-emerald-500",
  done: "border-l-emerald-500",
  failed: "border-l-red-500",
  "in progress": "border-l-emerald-500",
  in_progress: "border-l-emerald-500",
  pending: "border-l-gray-400",
  queued: "border-l-gray-400",
  running: "border-l-emerald-500",
  succeeded: "border-l-emerald-500",
  todo: "border-l-gray-400",
};

const DOT_MAP: Record<string, string> = {
  backlog: "bg-gray-400",
  blocked: "bg-gray-400",
  cancelled: "bg-amber-500",
  completed: "bg-emerald-500",
  doing: "bg-emerald-500",
  done: "bg-emerald-500",
  failed: "bg-red-500",
  "in progress": "bg-emerald-500",
  in_progress: "bg-emerald-500",
  pending: "bg-gray-400",
  queued: "bg-gray-400",
  running: "bg-emerald-500",
  succeeded: "bg-emerald-500",
  todo: "bg-gray-400",
};

/** Fuzzy fallback for free-form status strings not in the exact map. */
function fuzzyAccent(status: string): string {
  if (status.includes("done") || status.includes("complete")) {
    return "border-l-emerald-500";
  }
  if (status.includes("progress") || status.includes("doing")) {
    return "border-l-emerald-500";
  }
  if (status.includes("fail")) {
    return "border-l-red-500";
  }
  if (status.includes("block")) {
    return "border-l-gray-300";
  }
  return "border-l-gray-400";
}

function fuzzyDot(status: string): string {
  if (status.includes("done") || status.includes("complete")) {
    return "bg-emerald-500";
  }
  if (status.includes("progress") || status.includes("doing")) {
    return "bg-emerald-500";
  }
  if (status.includes("fail")) {
    return "bg-red-500";
  }
  if (status.includes("block")) {
    return "bg-gray-400";
  }
  return "bg-gray-400";
}

/** Get the `border-l-{color}` class for a status string. */
export function getStatusAccent(status: string | null | undefined): string {
  if (!status) {
    return "border-l-gray-400";
  }
  const key = status.toLowerCase();
  return ACCENT_MAP[key] ?? fuzzyAccent(key);
}

/** Get the `bg-{color}` class for the status indicator dot. */
export function getStatusDot(status: string | null | undefined): string {
  if (!status) {
    return "bg-gray-400";
  }
  const key = status.toLowerCase();
  return DOT_MAP[key] ?? fuzzyDot(key);
}

/** Whether the status represents an "active/running" state (used for pulse animation). */
export function isActiveStatus(status: string | null | undefined): boolean {
  if (!status) {
    return false;
  }
  const key = status.toLowerCase();
  return (
    key === "running" ||
    key === "in_progress" ||
    key === "in progress" ||
    key === "doing"
  );
}

/**
 * Get status dot configuration (color + pulse).
 * Canonical version; merges taskStatus.ts and statusColors.ts mappings.
 */
export function getStatusDotConfig(status: string): StatusDotConfig {
  const key = status.toLowerCase();

  // Exact mappings from both sources (preserving per-call-site behavior)
  switch (key) {
    case "succeeded":
    case "completed":
    case "done": {
      return { color: "bg-emerald-500", pulse: false };
    }
    case "running":
    case "in_progress":
    case "in progress":
    case "doing": {
      return { color: "bg-emerald-500", pulse: true };
    }
    case "queued":
    case "pending":
    case "todo":
    case "backlog": {
      return { color: "bg-amber-500", pulse: false };
    }
    case "failed":
    case "error": {
      // Conflict: taskStatus uses bg-rose-500, statusColors uses bg-red-500
      // Preserving taskStatus's rose-500 as primary (FlowTaskCard/TaskRow)
      return { color: "bg-rose-500", pulse: false };
    }
    case "canceled":
    case "cancelled": {
      // Conflict: taskStatus uses bg-rose-500, statusColors uses bg-amber-500
      // Preserving taskStatus's rose-500 as primary (FlowTaskCard/TaskRow)
      return { color: "bg-rose-500", pulse: false };
    }
    case "blocked": {
      return { color: "bg-gray-400", pulse: false };
    }
    default: {
      return { color: "bg-gray-400", pulse: false };
    }
  }
}

/**
 * Full border + bg + text classes for execution-graph node cards.
 * Returns a single string of Tailwind classes.
 */
const NODE_STYLE_MAP: Record<string, string> = {
  canceled:
    "border-rose-500 bg-rose-50 text-rose-700 dark:border-rose-400 dark:bg-rose-900/20 dark:text-rose-300",
  cancelled:
    "border-rose-500 bg-rose-50 text-rose-700 dark:border-rose-400 dark:bg-rose-900/20 dark:text-rose-300",
  failed:
    "border-rose-500 bg-rose-50 text-rose-700 dark:border-rose-400 dark:bg-rose-900/20 dark:text-rose-300",
  queued:
    "border-gray-400 bg-gray-100 text-gray-600 dark:border-gray-600 dark:bg-slate-800 dark:text-gray-300",
  running:
    "border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-900/20 dark:text-emerald-300 ring-2 ring-emerald-400/40 dark:ring-emerald-400/20",
  skipped:
    "border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-400 dark:bg-amber-900/20 dark:text-amber-200",
  succeeded:
    "border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-900/20 dark:text-emerald-300",
};

const DEFAULT_NODE_STYLE =
  "border-gray-400 bg-gray-100 text-gray-600 dark:border-gray-600 dark:bg-slate-800 dark:text-gray-300";

export function getStatusNodeStyle(status: string | null | undefined): string {
  if (!status) {
    return DEFAULT_NODE_STYLE;
  }
  return NODE_STYLE_MAP[status.toLowerCase()] ?? DEFAULT_NODE_STYLE;
}
